import { AnyAction } from 'redux';
import { DocumentStatus, fillLocalizedString } from '@synergy/models';

import {
    DEMAND_DETAIL_COMPLETED,
    DEMAND_DETAIL_RESET,
    DEMAND_DETAIL_SCHEDULED,
    DEMAND_DETAIL_STARTED,
} from '../demand-detail-actions';
import { DEMAND_DETAIL_OVERVIEW_RECEIVE, DEMAND_DETAIL_OVERVIEW_SUBMIT } from './demand-detail-overview-actions';
import { Demand } from '../../../../models/demand';
import { OverviewFormValues } from '../../models/overview-form-values';
import { StepData } from '../../../../../../common/components/new-stepper/step-data';

export interface DemandDetailOverviewReducerState {
    stepData: StepData<OverviewFormValues>;
    /**
     * The step as the server last handed it over (#2159).
     *
     * `stepData.values` cannot serve as the unsaved-changes baseline: leaving a
     * wizard step parks its input right there, so within a step change the two
     * become the same thing and nothing reads as unsaved any more.
     */
    baseline?: OverviewFormValues;
}

export const initialDemandDetailOverviewReducerState: DemandDetailOverviewReducerState = {
    stepData: {
        errors: {},
        values: { enableExpirationNotifications: false, status: DocumentStatus.Created },
        isValid: false,
        isDirty: false,
    },
};

export const demandDetailOverviewReducer = (
    state: DemandDetailOverviewReducerState = initialDemandDetailOverviewReducerState,
    action: AnyAction
): DemandDetailOverviewReducerState => {
    const { type } = action;

    switch (type) {
        case DEMAND_DETAIL_OVERVIEW_SUBMIT: {
            const data = action['data'] as StepData<OverviewFormValues>;
            return {
                ...state,
                stepData: {
                    ...state.stepData,
                    ...data,
                },
            };
        }

        case DEMAND_DETAIL_OVERVIEW_RECEIVE: {
            const demand = action['demand'] as Demand;
            const values: OverviewFormValues = {
                id: demand.id,
                status: demand.status,
                enableExpirationNotifications: demand.enableExpirationNotifications,
                description: fillLocalizedString(demand.description),
                endDate: demand.endDate,
                internalNote: demand.internalNote,
                noteToCustomer: fillLocalizedString(demand.noteToCustomer),
                responsibleContact: demand.responsibleContact,
                startDate: demand.startDate,
                title: fillLocalizedString(demand.title),
                isScheduled: demand.isScheduled ?? true,
            };

            return {
                ...state,
                stepData: {
                    ...state.stepData,
                    isValid: true,
                    values,
                },
                // The same values, kept apart from the ones the wizard edits.
                baseline: values,
            };
        }

        case DEMAND_DETAIL_STARTED: {
            return {
                ...state,
                stepData: {
                    ...state.stepData,
                    values: {
                        ...state.stepData.values,
                        status: DocumentStatus.Ongoing,
                    },
                },
            };
        }

        case DEMAND_DETAIL_COMPLETED: {
            return {
                ...state,
                stepData: {
                    ...state.stepData,
                    values: {
                        ...state.stepData.values,
                        status: DocumentStatus.Completed,
                    },
                },
            };
        }

        case DEMAND_DETAIL_SCHEDULED: {
            return {
                ...state,
                stepData: {
                    ...state.stepData,
                    values: {
                        ...state.stepData.values,
                        status: DocumentStatus.Scheduled,
                    },
                },
            };
        }

        case DEMAND_DETAIL_RESET: {
            return initialDemandDetailOverviewReducerState;
        }

        default: {
            return state;
        }
    }
};
